import { resolveProductReadinessEnabled } from "@/utils/accountProductFlags";

/** Query-param tab value for Organization employee drilldown (Assignments). */
export const ORG_EMPLOYEE_ASSIGNMENT_TAB = "positionshistory";

/** Default drilldown tab when the Readiness product module is on. */
export const ORG_EMPLOYEE_DEFAULT_TAB_READINESS_ON = "pathtoreadiness";

export const ORGANIZATION_EMPLOYEE_READINESS_ONLY_ROUTE_NAMES = Object.freeze([
  "NewOrganizationDetailEmployeeDashboard",
  "NewOrganizationEmployeePathToReadiness",
  "NewOrganizationEmployeeBadges",
]);

const READINESS_ONLY_QUERY_TABS = new Set([
  "dashboard",
  "pathtoreadiness",
  "badges",
  "responsibilities",
]);

/**
 * @param {object|null|undefined} [accountDetails] Prefer `$root.currentAccountDetails` for reactivity.
 * @returns {boolean}
 */
export function isOrganizationEmployeeReadinessProductEnabled(accountDetails) {
  return resolveProductReadinessEnabled(accountDetails);
}

/** @param {object|null|undefined} [accountDetails] @returns {string} */
export function getOrganizationEmployeeDefaultQueryTab(accountDetails) {
  if (!isOrganizationEmployeeReadinessProductEnabled(accountDetails)) {
    return ORG_EMPLOYEE_ASSIGNMENT_TAB;
  }
  return ORG_EMPLOYEE_DEFAULT_TAB_READINESS_ON;
}

/** @param {object|null|undefined} [accountDetails] @returns {boolean} */
export function shouldShowOrganizationEmployeeReadinessTabs(accountDetails) {
  return isOrganizationEmployeeReadinessProductEnabled(accountDetails);
}

/**
 * @param {boolean} showMdp609Flag
 * @param {object|null|undefined} [accountDetails]
 * @returns {boolean}
 */
export function shouldShowOrganizationEmployeeDashboardTab(showMdp609Flag, accountDetails) {
  return isOrganizationEmployeeReadinessProductEnabled(accountDetails) && showMdp609Flag === true;
}

/**
 * Assignments (Positions History) stays visible when Readiness product is off even if
 * the position-history LD flag is off.
 * @param {boolean} showPositionHistoryFlag
 * @param {object|null|undefined} [accountDetails]
 * @returns {boolean}
 */
export function shouldShowOrganizationEmployeeAssignmentsTab(
  showPositionHistoryFlag,
  accountDetails
) {
  return (
    showPositionHistoryFlag === true ||
    !isOrganizationEmployeeReadinessProductEnabled(accountDetails)
  );
}

/**
 * @param {string|number} employeeId
 * @returns {string}
 */
export function getOrganizationEmployeeAssignmentPath(employeeId) {
  return `/organization/employees/${employeeId}/assignment`;
}

/**
 * @param {{ name?: string, params?: { id?: string } }} route
 * @returns {boolean}
 */
export function isOrganizationEmployeeReadinessBlockedRoute(route, accountDetails) {
  if (isOrganizationEmployeeReadinessProductEnabled(accountDetails)) return false;
  const id = route?.params?.id;
  if (id == null || String(id).trim() === "") return false;

  if (route.name === "NewOrganizationDetailEmployee") return true;
  return ORGANIZATION_EMPLOYEE_READINESS_ONLY_ROUTE_NAMES.includes(route.name);
}

/**
 * @param {{ name?: string, params?: { id?: string } }} route
 * @returns {string|null}
 */
export function resolveOrganizationEmployeeReadinessRedirect(route, accountDetails) {
  if (!isOrganizationEmployeeReadinessBlockedRoute(route, accountDetails)) return null;
  return getOrganizationEmployeeAssignmentPath(route.params.id);
}

/**
 * @param {string|undefined|null} employeeTab
 * @param {boolean} showDashboardTab idea-MDP-609
 * @returns {string}
 */
export function normalizeOrganizationEmployeeQueryTab(
  employeeTab,
  showDashboardTab = false,
  accountDetails
) {
  const tab = String(employeeTab || "")
    .trim()
    .toLowerCase();

  if (!isOrganizationEmployeeReadinessProductEnabled(accountDetails)) {
    if (!tab || READINESS_ONLY_QUERY_TABS.has(tab)) {
      return ORG_EMPLOYEE_ASSIGNMENT_TAB;
    }
    return tab;
  }

  if (tab === "dashboard" && !showDashboardTab) {
    return ORG_EMPLOYEE_DEFAULT_TAB_READINESS_ON;
  }

  return tab || getOrganizationEmployeeDefaultQueryTab(accountDetails);
}

/**
 * Sidebar / horizontal menu labels gated by the Readiness product module.
 * @param {string} label
 * @returns {boolean}
 */
export function isOrganizationEmployeeReadinessMenuLabel(label) {
  const normalized = String(label || "")
    .trim()
    .toLowerCase();
  return (
    normalized === "dashboard" || normalized === "path to readiness" || normalized === "badges"
  );
}
